import { useState, useEffect } from "react";
import { X, MapPin, Globe, CheckSquare, Square, Check, Loader2, Search, Shield, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
} from "@/components/ui/drawer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  GlobalIntent,
  ALL_INTENTS,
  GlobalFilterPreferences,
  intentLabels,
  intentIcons,
} from "@/hooks/useGlobalIntents";
import { 
  useCountries, 
  useStates, 
  useSearchCities,
  useHasStates,
  useGlobalCitySearch
} from "@/hooks/useLocationData";
import { useDebounce } from "@/hooks/useDebounce";
import type { Country, State, City } from "@/types/location";

interface GlobalFilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  selectedIntents: GlobalIntent[];
  onIntentsChange: (intents: GlobalIntent[]) => void;
  filterPreferences: GlobalFilterPreferences;
  onFilterChange: (filters: GlobalFilterPreferences) => void;
  onApply: () => void;
  // Admin mode
  enableAdminMode?: boolean;
  isAdmin?: boolean;
}

export const GlobalFilterDrawer = ({
  isOpen,
  onClose,
  selectedIntents,
  onIntentsChange,
  filterPreferences,
  onFilterChange,
  onApply,
  enableAdminMode = false,
  isAdmin = false,
}: GlobalFilterDrawerProps) => {
  const [citySearch, setCitySearch] = useState("");
  const [countryOpen, setCountryOpen] = useState(false);
  const [stateOpen, setStateOpen] = useState(false);
  const [cityOpen, setCityOpen] = useState(false);
  const [globalSearchMode, setGlobalSearchMode] = useState(false);
  
  const debouncedCitySearch = useDebounce(citySearch, 300);

  // Local state for editing
  const [localIntents, setLocalIntents] = useState<GlobalIntent[]>(selectedIntents);
  const [localFilters, setLocalFilters] = useState<GlobalFilterPreferences>(filterPreferences);

  // Sync local state when drawer opens
  useEffect(() => {
    if (isOpen) {
      setLocalIntents(selectedIntents);
      setLocalFilters(filterPreferences);
      setGlobalSearchMode(false);
    }
  }, [isOpen, selectedIntents, filterPreferences]);

  // Use cached hooks for location data
  const { data: countries = [], isLoading: countriesLoading } = useCountries();
  const { data: states = [], isLoading: statesLoading } = useStates(localFilters.countryCode || null);
  const { hasStates } = useHasStates(localFilters.countryCode || null);
  
  // Standard city search
  const { data: standardCities = [], isLoading: standardCitiesLoading } = useSearchCities(
    localFilters.countryCode || null,
    localFilters.stateId || null,
    debouncedCitySearch,
    { isAdmin, bypassFilters: false }
  );
  
  // Global city search for admins
  const { data: globalCities = [], isLoading: globalCitiesLoading } = useGlobalCitySearch(
    debouncedCitySearch,
    isAdmin && globalSearchMode
  );
  
  // Use appropriate city list based on mode
  const cities = isAdmin && globalSearchMode ? globalCities : standardCities;
  const citiesLoading = isAdmin && globalSearchMode ? globalCitiesLoading : standardCitiesLoading;

  const selectedCountry = countries.find(c => c.code === localFilters.countryCode);
  const selectedState = states.find(s => s.id === localFilters.stateId);
  const selectedCity = cities.find(c => c.id === localFilters.cityId);

  // Check for exact match in search
  const hasExactMatch = cities.some(
    c => c.name.toLowerCase() === citySearch.toLowerCase().trim()
  );
  
  // Handle manual city entry
  const handleManualCityEntry = () => {
    if (!citySearch.trim()) return;
    setLocalFilters(prev => ({
      ...prev,
      cityId: `manual-${Date.now()}`,
      // Store the manual city name - we'll need to handle this in the filter logic
    }));
    setCityOpen(false);
    setCitySearch("");
  };

  // Show state selector if country has states
  const shouldShowState = hasStates && localFilters.countryCode && !globalSearchMode;

  const isAllIntentsSelected = localIntents.length === ALL_INTENTS.length;

  const toggleIntent = (intent: GlobalIntent) => {
    setLocalIntents((prev) =>
      prev.includes(intent)
        ? prev.filter((i) => i !== intent)
        : [...prev, intent]
    );
  };

  const toggleSelectAllIntents = () => {
    if (isAllIntentsSelected) {
      setLocalIntents([]);
    } else {
      setLocalIntents([...ALL_INTENTS]);
    }
  };

  const handleCountrySelect = (country: Country) => {
    setLocalFilters((prev) => ({
      ...prev,
      countryCode: country.code,
      stateId: undefined,
      cityId: undefined,
    }));
    setCountryOpen(false);
    setCitySearch("");
  };

  const handleStateSelect = (state: State) => {
    setLocalFilters((prev) => ({
      ...prev,
      stateId: state.id,
      cityId: undefined,
    }));
    setStateOpen(false);
    setCitySearch("");
  };

  const handleCitySelect = (city: City) => {
    setLocalFilters((prev) => ({
      ...prev,
      cityId: city.id,
    }));
    setCityOpen(false);
    setCitySearch("");
  };
  
  // Handle global city selection (includes country/state data)
  const handleGlobalCitySelect = (city: City & { country_name?: string; state_name?: string }) => {
    const country = countries.find((c) => c.code === city.country_code);
    
    setLocalFilters((prev) => ({
      ...prev,
      countryCode: city.country_code,
      stateId: city.state_id || undefined,
      cityId: city.id,
    }));
    setCityOpen(false);
    setCitySearch("");
    setGlobalSearchMode(false);
  };

  const handleClearLocation = () => {
    setLocalFilters((prev) => ({
      ...prev,
      countryCode: undefined,
      stateId: undefined,
      cityId: undefined,
    }));
    setCitySearch("");
    setGlobalSearchMode(false);
  };

  const handleApply = () => {
    onIntentsChange(localIntents);
    onFilterChange(localFilters);
    onApply();
    onClose();
  };

  const handleReset = () => {
    setLocalIntents(selectedIntents);
    setLocalFilters({});
    setCitySearch("");
    setGlobalSearchMode(false);
  };
  
  // Show city selector condition
  const showCitySelector = (isAdmin && globalSearchMode) || 
    (localFilters.countryCode && (!shouldShowState || localFilters.stateId || !hasStates));

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerHeader className="border-b border-border">
          <div className="flex items-center justify-between">
            <DrawerTitle>Global Filters</DrawerTitle>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-muted transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </DrawerHeader>

        <div className="overflow-y-auto p-4 space-y-6">
          {/* Intents Section */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <Globe className="w-4 h-4" />
              Connection Intent
            </h3>
            
            {/* Select All */}
            <button
              onClick={toggleSelectAllIntents}
              className={`w-full p-2.5 rounded-lg border transition-all text-left flex items-center gap-2 mb-2 ${
                isAllIntentsSelected
                  ? "border-primary bg-primary/10"
                  : "border-border hover:border-primary/50"
              }`}
            >
              {isAllIntentsSelected ? (
                <CheckSquare className="w-4 h-4 text-primary" />
              ) : (
                <Square className="w-4 h-4 text-muted-foreground" />
              )}
              <span className="text-sm font-medium">Select All</span>
            </button>

            <div className="flex flex-wrap gap-2">
              {ALL_INTENTS.map((intent) => {
                const isSelected = localIntents.includes(intent);
                return (
                  <button
                    key={intent}
                    onClick={() => toggleIntent(intent)}
                    className={`px-3 py-2 rounded-full border text-sm transition-all flex items-center gap-1.5 ${
                      isSelected
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border hover:border-primary/50"
                    }`}
                  >
                    <span>{intentIcons[intent]}</span>
                    <span>{intentLabels[intent].replace("Global ", "")}</span>
                    {isSelected && <Check className="w-3.5 h-3.5" />}
                  </button>
                );
              })}
            </div>
          </section>

          {/* Location Section */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3 flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              Location
            </h3>

            <div className="space-y-2">
              {/* Admin Global Search Toggle */}
              {enableAdminMode && isAdmin && (
                <div className="flex items-center justify-between p-2 rounded-md bg-amber-500/10 border border-amber-500/20 mb-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-4 w-4 text-amber-500" />
                    <span className="text-xs font-medium text-amber-700 dark:text-amber-400">
                      Global City Search
                    </span>
                  </div>
                  <Switch
                    checked={globalSearchMode}
                    onCheckedChange={setGlobalSearchMode}
                    className="scale-75"
                  />
                </div>
              )}
              
              {/* Global search indicator */}
              {isAdmin && globalSearchMode && (
                <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 mb-2">
                  <Globe className="h-3.5 w-3.5" />
                  <span>Searching all cities worldwide</span>
                </div>
              )}

              {/* Country Select */}
              {!globalSearchMode && (
                <>
              <label className="text-xs text-muted-foreground">Country</label>
              <Popover open={countryOpen} onOpenChange={setCountryOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start font-normal"
                    disabled={countriesLoading}
                  >
                    {countriesLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : selectedCountry ? (
                      <>
                        <span className="mr-2">{selectedCountry.flag}</span>
                        {selectedCountry.name}
                      </>
                    ) : (
                      <span className="text-muted-foreground">Any country</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[280px] p-0" align="start">
                  <div className="p-2 border-b">
                    <Input 
                      placeholder="Search countries..." 
                      className="h-8"
                      autoFocus
                    />
                  </div>
                  <ScrollArea className="h-[250px]">
                    <div className="p-1">
                      <button
                        onClick={() => {
                          setLocalFilters(prev => ({ ...prev, countryCode: undefined, stateId: undefined, cityId: undefined }));
                          setCountryOpen(false);
                        }}
                        className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent"
                      >
                        <Globe className="w-4 h-4" />
                        Any country
                      </button>
                      {countries.map((country) => (
                        <button
                          key={country.code}
                          onClick={() => handleCountrySelect(country)}
                          className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent ${
                            localFilters.countryCode === country.code ? "bg-accent" : ""
                          }`}
                        >
                          <span>{country.flag}</span>
                          <span className="truncate">{country.name}</span>
                          {localFilters.countryCode === country.code && (
                            <Check className="w-4 h-4 ml-auto text-primary" />
                          )}
                        </button>
                      ))}
                    </div>
                  </ScrollArea>
                </PopoverContent>
              </Popover>
                </>
              )}

              {/* State Select (conditional) */}
              {shouldShowState && (
                <>
                  <label className="text-xs text-muted-foreground">State / Province</label>
                  <Popover open={stateOpen} onOpenChange={setStateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start font-normal"
                        disabled={statesLoading}
                      >
                        {statesLoading ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : selectedState ? (
                          selectedState.name
                        ) : (
                          <span className="text-muted-foreground">Any state</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <ScrollArea className="h-[250px]">
                        <div className="p-1">
                          <button
                            onClick={() => {
                              setLocalFilters(prev => ({ ...prev, stateId: undefined, cityId: undefined }));
                              setStateOpen(false);
                            }}
                            className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent"
                          >
                            <Globe className="w-4 h-4" />
                            Any state
                          </button>
                          {states.map((state) => (
                            <button
                              key={state.id}
                              onClick={() => handleStateSelect(state)}
                              className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent ${
                                localFilters.stateId === state.id ? "bg-accent" : ""
                              }`}
                            >
                              <MapPin className="w-4 h-4 text-muted-foreground" />
                              <span className="truncate">{state.name}</span>
                              {localFilters.stateId === state.id && (
                                <Check className="w-4 h-4 ml-auto text-primary" />
                              )}
                            </button>
                          ))}
                        </div>
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                </>
              )}

              {/* City Select */}
              {showCitySelector && (
                <>
                  <label className="text-xs text-muted-foreground">
                    {isAdmin && globalSearchMode ? "City (Global)" : "City"}
                  </label>
                  <Popover open={cityOpen} onOpenChange={setCityOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full justify-start font-normal"
                      >
                        {selectedCity ? (
                          <>
                            <MapPin className="w-4 h-4 mr-2 text-primary" />
                            {selectedCity.name}
                            {isAdmin && globalSearchMode && selectedCity.country_name && (
                              <Badge variant="outline" className="ml-2 text-xs">
                                {selectedCity.country_name}
                              </Badge>
                            )}
                          </>
                        ) : (
                          <span className="text-muted-foreground">
                            {isAdmin && globalSearchMode ? "Type to search..." : "Search city..."}
                          </span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[280px] p-0" align="start">
                      <div className="p-2 border-b">
                        <div className="relative">
                          <Search className="absolute left-2 top-2 w-4 h-4 text-muted-foreground" />
                          <Input
                            placeholder={isAdmin && globalSearchMode ? "Type 2+ chars..." : "Type city name..."}
                            value={citySearch}
                            onChange={(e) => setCitySearch(e.target.value)}
                            className="h-8 pl-8"
                            autoFocus
                          />
                        </div>
                      </div>
                      <ScrollArea className="h-[200px]">
                        {citiesLoading ? (
                          <div className="flex items-center justify-center py-4">
                            <Loader2 className="w-4 h-4 animate-spin" />
                          </div>
                        ) : cities.length === 0 ? (
                          <div className="py-4 text-center text-sm text-muted-foreground">
                            {isAdmin && globalSearchMode ? (
                              debouncedCitySearch.length < 2 ? "Type 2+ characters" : "No cities found"
                            ) : (
                              debouncedCitySearch ? (
                                <div className="space-y-2">
                                  <p>No cities found in database</p>
                                  <button
                                    onClick={handleManualCityEntry}
                                    className="flex items-center gap-1.5 mx-auto px-3 py-1.5 text-sm rounded-md bg-primary/10 text-primary hover:bg-primary/20"
                                  >
                                    <Plus className="w-3.5 h-3.5" />
                                    Use "{debouncedCitySearch}"
                                  </button>
                                </div>
                              ) : "Type to search"
                            )}
                          </div>
                        ) : (
                          <div className="p-1">
                            {!globalSearchMode && (
                              <button
                              onClick={() => {
                                setLocalFilters(prev => ({ ...prev, cityId: undefined }));
                                setCityOpen(false);
                              }}
                              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent"
                              >
                              <Globe className="w-4 h-4" />
                              Any city
                              </button>
                            )}
                            {cities.map((city: City & { country_name?: string; state_name?: string }) => (
                              <button
                                key={city.id}
                                onClick={() => isAdmin && globalSearchMode ? handleGlobalCitySelect(city) : handleCitySelect(city)}
                                className={`w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded-md hover:bg-accent ${
                                  localFilters.cityId === city.id ? "bg-accent" : ""
                                }`}
                              >
                                <MapPin className="w-4 h-4 text-muted-foreground" />
                                <span className="truncate flex-1">{city.name}</span>
                                {isAdmin && globalSearchMode && (city.state_name || city.country_name) && (
                                  <span className="text-xs text-muted-foreground truncate">
                                    {[city.state_name, city.country_name].filter(Boolean).join(", ")}
                                  </span>
                                )}
                                {localFilters.cityId === city.id && (
                                  <Check className="w-4 h-4 ml-auto text-primary" />
                                )}
                              </button>
                            ))}
                            
                            {/* Manual city entry option */}
                            {citySearch.trim() && !hasExactMatch && (
                              <button
                                onClick={handleManualCityEntry}
                                className="w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md hover:bg-primary/10 text-primary border-t mt-1 pt-2"
                              >
                                <Plus className="w-4 h-4" />
                                <span>Use "{citySearch.trim()}"</span>
                              </button>
                            )}
                          </div>
                        )}
                      </ScrollArea>
                    </PopoverContent>
                  </Popover>
                </>
              )}

              {/* Clear location button */}
              {(localFilters.countryCode || (isAdmin && globalSearchMode)) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearLocation}
                  className="text-xs text-muted-foreground h-7"
                >
                  <X className="w-3 h-3 mr-1" />
                  Clear location
                </Button>
              )}

              {/* Nearby Toggle */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Nearby Only</p>
                  <p className="text-xs text-muted-foreground">
                    Show users within distance
                  </p>
                </div>
                <Switch
                  checked={localFilters.nearbyEnabled || false}
                  onCheckedChange={(checked) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      nearbyEnabled: checked,
                      nearbyRadius: checked ? prev.nearbyRadius || 50 : undefined,
                    }))
                  }
                />
              </div>

              {/* Nearby Radius */}
              {localFilters.nearbyEnabled && (
                <div>
                  <div className="flex justify-between mb-2">
                    <span className="text-xs text-muted-foreground">Radius</span>
                    <span className="text-xs font-medium">
                      {localFilters.nearbyRadius || 50} miles
                    </span>
                  </div>
                  <Slider
                    value={[localFilters.nearbyRadius || 50]}
                    onValueChange={([value]) =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        nearbyRadius: value,
                      }))
                    }
                    min={10}
                    max={500}
                    step={10}
                  />
                </div>
              )}
            </div>
          </section>

          {/* Preferences Section */}
          <section>
            <h3 className="text-sm font-semibold text-muted-foreground mb-3">
              Preferences
            </h3>

            <div className="space-y-4">
              {/* Age Range */}
              <div>
                <div className="flex justify-between mb-2">
                  <span className="text-xs text-muted-foreground">Age Range</span>
                  <span className="text-xs font-medium">
                    {localFilters.ageMin || 18} - {localFilters.ageMax || 65}
                  </span>
                </div>
                <div className="flex gap-3 items-center">
                  <Slider
                    value={[localFilters.ageMin || 18, localFilters.ageMax || 65]}
                    onValueChange={([min, max]) =>
                      setLocalFilters((prev) => ({
                        ...prev,
                        ageMin: min,
                        ageMax: max,
                      }))
                    }
                    min={18}
                    max={65}
                    step={1}
                  />
                </div>
              </div>

              {/* Verified Only */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Verified Only</p>
                  <p className="text-xs text-muted-foreground">
                    Show verified profiles only
                  </p>
                </div>
                <Switch
                  checked={localFilters.verifiedOnly || false}
                  onCheckedChange={(checked) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      verifiedOnly: checked,
                    }))
                  }
                />
              </div>

              {/* Online Only */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium">Online Now</p>
                  <p className="text-xs text-muted-foreground">
                    Show users currently online
                  </p>
                </div>
                <Switch
                  checked={localFilters.onlineOnly || false}
                  onCheckedChange={(checked) =>
                    setLocalFilters((prev) => ({
                      ...prev,
                      onlineOnly: checked,
                    }))
                  }
                />
              </div>
            </div>
          </section>
        </div>

        <DrawerFooter className="border-t border-border">
          <div className="flex gap-3">
            <Button variant="outline" onClick={handleReset} className="flex-1">
              Reset
            </Button>
            <Button
              onClick={handleApply}
              className="flex-1"
              disabled={localIntents.length === 0}
            >
              Apply Filters
            </Button>
          </div>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
